import deepmerge from "deepmerge";
import { configToGlue, processManifestMacros, expandConfig } from "../../api-polyfill/src/common/configToGlue";
import { objectFlatMap, traverse, FINSEMBLECONFIG, FINSEMBLECONFIGAPPS, FINSEMBLEMACROS, safeInteropRegister, deleteNullish, asyncForEach, Gate } from "../../api-polyfill/src/common/windowFunctions";
import coreConfig from "@finsemble/finsemble-core/dist/platform/configs/core/config.json";
import uiComponentsConfig from "@finsemble/finsemble-core/dist/platform/configs/core/UIComponents.json";
import securityPoliciesConfig from "@finsemble/finsemble-core/dist/platform/configs/core/securityPolicies.json";
import servicesConfig from "@finsemble/finsemble-core/dist/platform/configs/core/services.json";
import workspacesConfig from "@finsemble/finsemble-core/dist/platform/configs/core/workspaces.json";
import rawSchema from "@finsemble/finsemble-core/dist/platform/configs/schemas/finsemble.schema.json";
import { getDiscardUnsavedChangesSetting, scheduledCloseDiscardUnsavedChanges } from "../../api-polyfill/src/config/preferences";
import { isValidURL } from "../../api-polyfill/src/utils/validateURL";
const defaultManifest = {
    splashScreenImage: "$documentRoot/assets/img/FinsembleSplash.png",
    finsemble: {
        documentRoot: "../../",
        applicationRoot: "$documentRoot/build",
        configRoot: "$documentRoot/configs",
        moduleRoot: "$applicationRoot/finsemble",
        servicesRoot: "$moduleRoot/services",
        configSchemaURL: "$moduleRoot/configs/schemas/finsemble.schema.json",
        systemTrayIcon: "$documentRoot/assets/img/Finsemble_SystemTray_Icon.png"
    },
    startup_app: {
        applicationIcon: "$documentRoot/assets/img/Finsemble_Taskbar_Icon.png",
    }
};
const filterUnusedComponents = (config) => {
    const noLongerNeeded = ["bloombergBridgeService", "LinkerWindow", "QuickComponentForm", "NonConfiguredComponent", "DockingGroupMask", "ProcessMonitor", "Download Manager", "AppCatalog", "HighlightScrim", "FDC3Resolver", "NotificationsCenter", "NotificationsToasts"];
    if (!config?.finsemble?.components) {
        return;
    }
    config.finsemble.components = objectFlatMap(config.finsemble.components, (key, value) => noLongerNeeded.includes(key) ? [] : [[key, value]]);
};
const fetchConfigFile = async (url, logger) => {
    let err = null;
    let data;
    try {
        const response = await globalThis.fetch(url, {
            credentials: "same-origin",
            cache: "no-cache"
        });
        if (response.status !== 200) {
            err = `config - HTTP get error for ${url}: ${response.status} ${response.statusText}`;
        }
        else {
            data = await response.json();
        }
    }
    catch (error) {
        err = `config - Failure fetching config from ${url} ${error.toString()}`;
        logger.error(err, error);
    }
    return { err, data };
};
const applyDefault = (path, obj, defaultValue) => {
    if (path.length === 1) {
        if (path[0] === "*") {
            return;
        }
        if (typeof obj !== "object") {
            return;
        }
        if (obj[path[0]] === undefined) {
            obj[path[0]] = defaultValue;
        }
        return;
    }
    if (path[0] === "*") {
        Object.keys(obj).forEach((key) => {
            applyDefault(path.slice(1), obj[key], defaultValue);
        });
    }
    else {
        if (!obj[path[0]]) {
            obj[path[0]] = {};
        }
        if (Array.isArray(obj[path[0]])) {
            Object.values(obj[path[0]]).forEach((value) => {
                applyDefault(path.slice(1), value, defaultValue);
            });
            return;
        }
        if (typeof obj[path[0]] !== "object") {
            return;
        }
        applyDefault(path.slice(1), obj[path[0]], defaultValue);
    }
};
const applyDefaults = (schema, config) => {
    if (!config) {
        return;
    }
    const recurse = (path, visited, branch, forceDefaults) => {
        if (visited.has(branch)) {
            return;
        }
        visited.add(branch);
        if (branch.default && forceDefaults) {
            applyDefault(path, config, branch.default);
        }
        if (branch.properties) {
            Object.entries(branch.properties).forEach((entry) => {
                const [key, value] = entry;
                recurse(path.concat(key), new Set(visited), value, branch.forceDefaults ?? forceDefaults);
            });
        }
        if (typeof branch.additionalProperties === "object") {
            Object.entries(branch.additionalProperties.properties ?? {}).forEach((entry) => {
                const [, value] = entry;
                recurse(path.concat("*"), new Set(visited), value, branch.additionalProperties.forceDefaults ?? forceDefaults);
            });
        }
    };
    recurse([], new Set(), schema, true);
};
const resolveToAbsoluteURL = (baseURL, urlString, logger) => {
    if (!baseURL) {
        return urlString;
    }
    let result = urlString;
    if (typeof urlString === "string" && urlString?.length && urlString.charAt(0) !== "$") {
        try {
            result = new URL(urlString, baseURL).href;
            result = result.replace(/([^:]\/)\/+/g, "$1");
            if (result.slice(-1)[0] === "/") {
                result = result.slice(0, -1);
            }
        }
        catch (err) {
            logger.error(err);
            return urlString;
        }
    }
    return result;
};
const computeManifestMacros = (config, logger, manifestURL) => {
    logger.debug("config - computeManifestMacros");
    let macros = {};
    const seedPossibleMacroValues = () => {
        macros = {};
        const possibleMacroAreas = deepmerge.all([{}, config?.finsemble, config?.finsemble?.custom ?? {}]);
        Object.entries(possibleMacroAreas).forEach((entry) => {
            const [key, value] = entry;
            if (typeof value === "string" && value !== "$schema") {
                if (isValidURL(value)) {
                    macros[`$${key}`] = resolveToAbsoluteURL(manifestURL, value, logger);
                }
                else {
                    macros[`$${key}`] = value;
                }
            }
        });
    };
    const seedPossibleEnvValues = () => {
        const w = globalThis;
        const allowedEnv = config["finsemble-electron-adapter"]?.["env-allowlist"] ?? [];
        allowedEnv.forEach((env) => macros[`$${env}`] = w.glue42gd?.env[env]);
    };
    seedPossibleMacroValues();
    seedPossibleEnvValues();
    logger.debug("config - computeManifestMacros=", macros);
    return macros;
};
const getUrlExtension = (url) => {
    try {
        const pathName = new URL(url).pathname;
        const fileName = pathName.replace(/^.*[\\/]/, "");
        const maybeExtension = fileName.split(".").pop() ?? null;
        return maybeExtension === fileName ? null : maybeExtension;
    }
    catch (e) {
        return null;
    }
};
export const calculateWindowTitleBarUrl = (url, extension) => {
    if (!url) {
        return url;
    }
    const existingExtension = getUrlExtension(url);
    if (existingExtension) {
        return `${url.substr(0, url.lastIndexOf("."))}.${extension}`;
    }
    const entryWithSlash = url.replace(/\/?$/, "/");
    return `${entryWithSlash}index.${extension}`;
};
export class ConfigService {
    constructor(glue, logger, finsembleServiceContext) {
        this.glue = glue;
        this.logger = logger;
        this.finsembleServiceContext = finsembleServiceContext;
        this.schema = deepmerge({}, rawSchema);
        this.config = {};
        this.macros = {};
        this.gate = new Gate();
    }
    async updateContext() {
        const configWithoutApps = {
            ...this.config,
            finsemble: {
                ...this.config.finsemble,
            }
        };
        delete configWithoutApps.finsemble.apps;
        delete configWithoutApps.finsemble.components;
        await this.glue.contexts.set(FINSEMBLECONFIG, configWithoutApps);
        await this.glue.contexts.set(FINSEMBLECONFIGAPPS, {
            finsemble: {
                apps: this.config?.finsemble?.apps,
                components: this.config?.finsemble?.components
            }
        });
    }
    normalizeSchema() {
        traverse(this.schema, ({ obj, key, value }) => {
            if (key === "$ref") {
                const definitionName = value.split("#/definitions/").pop();
                delete obj[key];
                Object.entries(this.schema.definitions[definitionName]).forEach((entry) => {
                    obj[entry[0]] = entry[1];
                });
            }
        });
        delete this.schema.definitions;
    }
    async recursiveLoadImport(url, isManifest, logger) {
        const customMerge = (key) => {
            if (key === "workspaces")
                return (target, source) => source;
            return undefined;
        };
        this.logger.debug("config - recursiveLoadImport");
        if (!url) {
            return [];
        }
        let errs = [];
        const { err, data } = await fetchConfigFile(url, logger);
        if (!err && data) {
            this.config = deepmerge(this.config, isManifest ? data : {
                finsemble: data
            }, { customMerge });
            errs = await this.processConfig(logger, this.finsembleServiceContext.manifestURL);
        }
        else if (err) {
            errs.push(err.toString());
        }
        return errs;
    }
    dupAppsToComponents() {
        this.config.finsemble.components = this.config.finsemble.components ?? {};
        this.config.finsemble.apps.forEach((app) => {
            this.config.finsemble.components[app.appId] = app.hostManifests?.Finsemble ?? {};
        });
    }
    async processConfig(logger, manifestURL) {
        this.macros = computeManifestMacros(this.config, logger, manifestURL);
        this.config = processManifestMacros(this.macros, this.config, logger);
        const promises = this.config.finsemble?.importConfig?.map((importConfig) => this.recursiveLoadImport(resolveToAbsoluteURL(manifestURL, importConfig, logger), false, logger)) ?? [];
        delete this.config.finsemble?.importConfig;
        const results = await Promise.allSettled(promises);
        return results.flatMap((result) => result.status === "fulfilled" ? result.value : result.reason);
    }
    async finalProcessingPass({ appIds, additionalApps = [] }) {
        this.config.finsemble.apps = this.config.finsemble.apps ?? [];
        this.logger.debug("config - Apps to be converted (from Finsemble apps.json format)", this.config.finsemble.apps);
        this.logger.debug("config - finalProcessingPass: filterUnusedComponents");
        filterUnusedComponents(this.config);
        this.logger.debug("config - finalProcessingPass: expandConfig");
        expandConfig(this.config);
        this.logger.debug("config - finalProcessingPass: applyDefaults");
        applyDefaults(this.schema, this.config);
        this.logger.debug("config - finalProcessingPass: configToGlue", appIds);
        const definitions = configToGlue(this.glue, this.logger, this.config, appIds).concat(additionalApps);
        this.logger.debug("config - Apps added by config service", definitions);
        const result = await this.glue.appManager.inMemory.import(definitions, "merge");
        this.logger.debug("config - Finished inMemory import");
        const errors = result.errors.map((error) => error.app + " : " + error.error);
        this.logger.debug("config - finalProcessingPass: dupAppsToComponents");
        this.dupAppsToComponents();
        this.updateContext();
        this.glue.contexts.set(FINSEMBLEMACROS, this.macros);
        const err = errors.length ? errors.join("\n") : null;
        if (err) {
            this.logger.error("config - finalProcessingPath error from configToGlue", errors);
        }
        return { err };
    }
    async additionalIOCDApps() {
        const decoratorWindowUrl = this.config.finsemble?.decoratorWindowUrl;
        const webGroupApplication = {
            "name": "web-group-application",
            "title": "Web group native implementation",
            "type": "webGroup",
            "disabled": false,
            "hidden": true,
            "captureErrors": true,
            "details": {
                "url": calculateWindowTitleBarUrl(decoratorWindowUrl, "html")
            }
        };
        if (!webGroupApplication.details.url) {
            delete webGroupApplication.details.url;
        }
        const enterPostSSOStage = {
            "title": "Enter PostSSO Stage",
            "type": "window",
            "name": "EnterPostSSOStage",
            "hidden": true,
            "ignoreSavedLayout": true,
            "ignoreFromLayouts": true,
            "service": false,
            "autoStart": true,
            "runPriority": {
                "stage": "post-sso",
                "priority": 8000,
                "waitForInitialization": true
            },
            "details": {
                "url": new URL("EnterBootStage.html", window.location.href).href,
                "isSticky": false,
                "height": 200,
                "width": 200,
                "left": 200,
                "top": 200,
                "hidden": true,
                "mode": "html",
                "channelSelector": {
                    "enabled": false
                },
                "allowFeedback": false,
                "allowClose": false,
                "allowMinimize": false,
                "allowMaximize": false,
                "context": {
                    "stage": "post-sso"
                }
            }
        };
        const enterUserStage = {
            ...enterPostSSOStage,
            "title": "Enter User Stage",
            "name": "EnterUserStage",
            "runPriority": {
                "stage": "post-sso",
                "priority": 1,
                "waitForInitialization": true
            },
            details: {
                ...enterPostSSOStage.details,
                "context": {
                    "stage": "user"
                }
            }
        };
        const additionalApps = [enterPostSSOStage, enterUserStage];
        if (decoratorWindowUrl) {
            additionalApps.push(webGroupApplication);
        }
        this.logger.debug("config - additionalIOCDApps", additionalApps);
        return { data: additionalApps };
    }
    async loadConfigAtBoot(logger) {
        const { manifestURL } = this.finsembleServiceContext;
        const { documentRoot = new URL("../..", manifestURL).href } = this.finsembleServiceContext;
        this.logger.debug("config - documentRoot", documentRoot);
        const defaultConfig = {
            finsemble: deepmerge.all([coreConfig, uiComponentsConfig, workspacesConfig, securityPoliciesConfig, servicesConfig])
        };
        this.config = deepmerge.all([defaultManifest, defaultConfig]);
        delete this.config.finsemble.importConfig;
        this.config.finsemble.documentRoot = documentRoot;
        logger.info(`config - Loading manifest from ${this.finsembleServiceContext.manifestURL}`);
        const errors = await this.recursiveLoadImport(this.finsembleServiceContext.manifestURL, true, logger);
        const { err: err2, data: additionalApps } = await this.additionalIOCDApps();
        const { err } = await this.finalProcessingPass({ additionalApps });
        return { err: err ?? err2 ?? errors.length ? errors.join("\n") : null };
    }
    processMacros(app) {
        const replaceMacros = (value) => {
            Object.keys(this.macros).forEach((macro) => {
                value = value.replace(macro, this.macros[macro]);
            });
            return value;
        };
        const replaceLevel = (obj) => {
            if (Array.isArray(obj)) {
                return obj.map((item) => replaceLevel(item));
            }
            else if (typeof (obj) === "object") {
                const notNullishObj = deleteNullish(obj);
                return objectFlatMap(notNullishObj, (key, value) => {
                    return [[key, replaceLevel(value)]];
                });
            }
            else if (typeof (obj) === "string") {
                return replaceMacros(obj);
            }
            return obj;
        };
        const newApp = replaceLevel(app);
        Object.keys(newApp).forEach((key) => {
            app[key] = newApp[key];
        });
    }
    listenForAddApp() {
        this.glue.interop.register("FSBL.Clients.AppsClient.addApps", safeInteropRegister(async (params, caller) => {
            this.logger.debug("config - FSBL.Clients.AppsClient.addApps start. Waiting at gate.");
            await this.gate.wait(async () => {
                this.logger.debug("config - FSBL.Clients.AppsClient.addApps through gate");
                params.apps.forEach((app) => {
                    this.processMacros(app);
                    this.config.finsemble.apps = (this.config.finsemble.apps ?? []).filter((oldApp) => oldApp.appId !== app.appId).concat(app);
                });
                await this.finalProcessingPass({ appIds: params.apps.map((app) => app.appId) });
                this.logger.debug("config - FSBL.Clients.AppsClient.addApps end");
            });
        }, this.logger));
    }
    listenForRemoveApp() {
        this.glue.interop.register("FSBL.Clients.AppsClient.removeApps", safeInteropRegister(async (params, caller) => {
            this.logger.debug("config - FSBL.Clients.AppsClient.removeApps start");
            this.config.finsemble.apps = this.config.finsemble.apps.filter((oldApp) => !params.appIds.some((appId) => appId === oldApp.appId));
            params.appIds.forEach((appId) => {
                delete this.config.finsemble.components?.[appId];
            });
            await this.updateContext();
            this.logger.debug("config - FSBL.Clients.AppsClient.removeApps end");
        }, this.logger));
    }
    listenForProcessAndSet() {
        this.glue.interop.register("FSBL.Clients.ConfigClient.processAndSet", safeInteropRegister(async (params, caller) => {
            this.logger.debug("config - FSBL.Clients.AppsClient.processAndSet start");
            console.debug("config - Received processAndSet", params);
            const { newConfig, replace, overwrite } = params;
            const { manifestURL } = await this.glue.windows.my()?.getContext();
            const appIds = (newConfig.apps ?? []).map((app) => app.appId);
            if (replace) {
                const glueConfigs = await this.glue.appManager.getConfigurations();
                await asyncForEach(glueConfigs, async (config) => {
                    if (config.customProperties?.appd && !config.customProperties.appd.hostManifests?.finsemble?.appService) {
                        const { appId } = config.customProperties.appd;
                        await this.glue.appManager.inMemory.remove(appId);
                        this.config.finsemble.apps = this.config.finsemble.apps.filter((existingApp) => existingApp.appId !== appId);
                    }
                });
            }
            else if (overwrite) {
                await asyncForEach(appIds, async (appId) => {
                    await this.glue.appManager.inMemory.remove(appId);
                    this.config.finsemble.apps = this.config.finsemble.apps.filter((existingApp) => existingApp.appId !== appId);
                });
            }
            else {
                const dups = this.config.finsemble.apps.some((existingApp) => appIds.includes(existingApp.appId));
                if (dups) {
                    const err = "processAndSet() contains duplicate app entries but overwrite and replace flags are both false";
                    return { err };
                }
            }
            console.debug("config - processAndSet: config after removals", this.config);
            const arrayMerge = (target, source, options) => (target ?? []).concat(source);
            this.config.finsemble = deepmerge(this.config.finsemble, newConfig, { arrayMerge });
            console.debug("config - processAndSet: config after deepmerge", this.config);
            const errors = await this.processConfig(this.logger, manifestURL);
            const { err } = await this.finalProcessingPass({ appIds });
            this.logger.debug("config - processAndSet: apps after final processing pass (see devtools for more debugging)", this.config.finsemble.apps.map((app) => app.appId));
            console.debug("config - processAndSet: config after final processing pass", this.config);
            this.logger.debug("config - FSBL.Clients.AppsClient.processAndSet end");
            return { err: err ?? errors.length ? errors.join("\n") : null, data: this.config.finsemble };
        }, this.logger));
    }
    async maybeSetScheduledShutdownAndRestart() {
        const discard = await getDiscardUnsavedChangesSetting(this.glue);
        await scheduledCloseDiscardUnsavedChanges(this.glue, discard, (...args) => this.logger.info(...args));
    }
    async start() {
        this.normalizeSchema();
        const { err } = await this.loadConfigAtBoot(this.logger);
        this.logger.debug("config - Initial set of apps loaded from Finsemble config");
        if (err) {
            this.logger.error("config - Error starting config service: ", err.toString());
        }
        this.listenForProcessAndSet();
        this.listenForAddApp();
        this.listenForRemoveApp();
    }
}
